import type {
    LibraryDiagram,
    LibraryImage,
    LibraryVideo,
} from '@modules/product/type/product-gallery-types';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import { useInViewport } from 'ahooks';
import React, { useEffect } from 'react';

import ImageSlide from './ImageSlide';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import BottomDrawer from '~/modules/detail/components/bottom-drawer/BottomDrawer';
import { Link } from '@remix-run/react';
import { pxToRem, useStyles } from '@archipro-design/aria';
import * as S from './ProductViewerModal.mobile.style';

interface PhotoViewerModalProps {
    stack: Array<LibraryImage | LibraryVideo | LibraryDiagram>;
    selectedSlide: number;
    open: boolean;
    toggle: () => void;
    productTitle: string;
    professional: {
        Title: string;
        Link: string;
    };
}

const PhotoViewerModalMobile = ({
    stack,
    selectedSlide,
    open,
    toggle,
    professional,
    productTitle,
}: PhotoViewerModalProps) => {
    const { css } = useStyles();
    const containerRef = React.useRef(null);
    const contentRef = React.useRef<(HTMLDivElement | null)[]>([]);

    const [inViewport] = useInViewport(containerRef, {
        threshold: 0,
        rootMargin: '100%',
    });

    const slides = stack.flatMap((slide, idx) => {
        if (isLibraryVideo(slide)) return [];
        const libraryImageSrc =
            slide.LibraryImageFile?.DetailPageSquare.Mobile ?? '';
        return (
            <div style={{ marginTop: idx !== 0 ? pxToRem(18) : 0 }}>
                <ImageSlide
                    key={slide.ID}
                    libraryImage={libraryImageSrc}
                    height={378}
                    width={378}
                    objectFit="contain"
                    objectPosition="center"
                    ref={(ref) => (contentRef.current[idx] = ref)}
                    sizes={getImagesSizes(1768)}
                    loadingValue="lazy"
                />
                {!!slide.Caption && (
                    <div
                        className={css(S.CaptionWrapper)}
                        dangerouslySetInnerHTML={{ __html: slide.Caption }}
                    ></div>
                )}
            </div>
        );
    });

    useEffect(() => {
        if (!inViewport) return;

        const element = contentRef.current[selectedSlide];
        if (element && selectedSlide > 0) {
            element.scrollIntoView({
                behavior: 'smooth',
            });
        }
    }, [inViewport, selectedSlide]);

    return (
        <BottomDrawer
            contentRef={containerRef}
            open={open}
            toggleModal={() => toggle()}
            title={
                <>
                    <div>{productTitle}</div>
                    <div className={css(S.SubtitleWrapper)}>
                        <Link
                            to={professional.Link}
                            className={css(S.Subtitle)}
                            prefetch="intent"
                        >
                            By {professional.Title}
                        </Link>
                    </div>
                </>
            }
        >
            <div className={css(S.SlidesWrapper)}>{slides}</div>
        </BottomDrawer>
    );
};

export default PhotoViewerModalMobile;
